/**
 * AdminAdmissionKitsTab — single hub for the entire admission flow.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  [ Enviar PDF do Kit ]                       │
 *   ├──────────────────────────────────────────────┤
 *   │ ▣ Contratos │ EPI │ Crachá │ VT              │
 *   ├──────────────────────────────────────────────┤
 *   │  <selected sub-tab content>                  │
 *   └──────────────────────────────────────────────┘
 */
import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import {
  UploadCloud, Loader2, FileSignature, HardHat, IdCard, Bus,
  Building2, Building, Lock, ClipboardCheck, FileText, Sparkles, Users,
  HeartPulse,
} from "lucide-react";
import { API } from "../App";
import AdminContractsTab from "./AdminContractsTab";
import AdminEpiRecordsTab from "./AdminEpiRecordsTab";
import AdminBadgeDeclarationsTab from "./AdminBadgeDeclarationsTab";
import AdminVTDeclarationsTab from "./AdminVTDeclarationsTab";
import AdminNaoAdesaoTab from "./AdminNaoAdesaoTab";
import AdminKitDocSimpleTab from "./AdminKitDocSimpleTab";
import AdminAdmissionKitsByEmployeeTab from "./AdminAdmissionKitsByEmployeeTab";
import KitUploadProgressDialog from "./KitUploadProgressDialog";
import KitSmartSplitDialog from "./KitSmartSplitDialog";

const AdminAdmissionKitsTab = ({ adminToken }) => {
  const [uploading, setUploading] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressUploadId, setProgressUploadId] = useState("");
  const [smartSplitOpen, setSmartSplitOpen] = useState(false);
  const inputRef = useRef(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie apenas arquivos PDF");
      return;
    }
    const fd = new FormData();
    fd.append("files", file);
    setUploading(true);
    try {
      const res = await axios.post(
        `${API}/admin/admission-kits/upload`, fd,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000,
        },
      );
      const uid = res.data?.upload_id;
      if (uid) {
        setProgressUploadId(uid);
        setProgressOpen(true);
      } else {
        toast.error("Resposta inesperada do servidor");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload button */}
      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          onChange={onUpload}
          className="hidden"
          data-testid="kit-upload-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="kit-upload-btn"
        >
          {uploading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</>
            : <><UploadCloud className="w-4 h-4 mr-2" />Enviar PDF do Kit</>}
        </Button>
        <Button
          variant="outline"
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
          onClick={() => setSmartSplitOpen(true)}
          data-testid="kit-smart-split-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Smart Splitter (PDF escaneado)
        </Button>
      </div>

      {/* Documentos do kit (sub-abas) */}
      <Tabs defaultValue="por_funcionario" className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 justify-start">
          <TabsTrigger value="por_funcionario" className="flex items-center gap-1.5 data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="kit-subtab-por-funcionario">
            <Users className="w-4 h-4" />
            Por Funcionário
          </TabsTrigger>
          <TabsTrigger value="contratos" className="flex items-center gap-1.5" data-testid="kit-subtab-contratos">
            <FileSignature className="w-4 h-4" />
            Contratos
          </TabsTrigger>
          <TabsTrigger value="epi" className="flex items-center gap-1.5" data-testid="kit-subtab-epi">
            <HardHat className="w-4 h-4" />
            EPI
          </TabsTrigger>
          <TabsTrigger value="cracha" className="flex items-center gap-1.5" data-testid="kit-subtab-cracha">
            <IdCard className="w-4 h-4" />
            Crachá
          </TabsTrigger>
          <TabsTrigger value="vt" className="flex items-center gap-1.5" data-testid="kit-subtab-vt">
            <Bus className="w-4 h-4" />
            VT
          </TabsTrigger>
          <TabsTrigger value="nao_adesao" className="flex items-center gap-1.5" data-testid="kit-subtab-nao_adesao">
            <HeartPulse className="w-4 h-4" />
            Adesão Plano de Saúde
          </TabsTrigger>
          <TabsTrigger value="integracao_matriz" className="flex items-center gap-1.5" data-testid="kit-subtab-integracao_matriz">
            <Building2 className="w-4 h-4" />
            Integração Matriz
          </TabsTrigger>
          <TabsTrigger value="integracao_filial" className="flex items-center gap-1.5" data-testid="kit-subtab-integracao_filial">
            <Building className="w-4 h-4" />
            Integração Filial
          </TabsTrigger>
          <TabsTrigger value="lgpd" className="flex items-center gap-1.5" data-testid="kit-subtab-lgpd">
            <Lock className="w-4 h-4" />
            LGPD
          </TabsTrigger>
          <TabsTrigger value="ordem_servico" className="flex items-center gap-1.5" data-testid="kit-subtab-ordem_servico">
            <ClipboardCheck className="w-4 h-4" />
            Ordem de Serviço
          </TabsTrigger>
          <TabsTrigger value="ficha_registro" className="flex items-center gap-1.5" data-testid="kit-subtab-ficha_registro">
            <FileText className="w-4 h-4" />
            Ficha de Registro
          </TabsTrigger>
        </TabsList>

        <TabsContent value="por_funcionario" className="mt-4">
          <AdminAdmissionKitsByEmployeeTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="contratos" className="mt-4">
          <AdminContractsTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="epi" className="mt-4">
          <AdminEpiRecordsTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="cracha" className="mt-4">
          <AdminBadgeDeclarationsTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="vt" className="mt-4">
          <AdminVTDeclarationsTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="nao_adesao" className="mt-4">
          <AdminNaoAdesaoTab adminToken={adminToken} />
        </TabsContent>
        <TabsContent value="integracao_matriz" className="mt-4">
          <AdminKitDocSimpleTab
            adminToken={adminToken} kind="integracao_matriz"
            title="Integração — Matriz"
            description="Formulário de integração da unidade Matriz (Salvador/BA)."
          />
        </TabsContent>
        <TabsContent value="integracao_filial" className="mt-4">
          <AdminKitDocSimpleTab
            adminToken={adminToken} kind="integracao_filial"
            title="Integração — Filial"
            description="Formulário de integração da unidade Filial (São Paulo/SP)."
          />
        </TabsContent>
        <TabsContent value="lgpd" className="mt-4">
          <AdminKitDocSimpleTab
            adminToken={adminToken} kind="lgpd"
            title="Termo LGPD"
            description="Termo de Tratamento de Dados Pessoais (Lei 13.709/2018)."
          />
        </TabsContent>
        <TabsContent value="ordem_servico" className="mt-4">
          <AdminKitDocSimpleTab
            adminToken={adminToken} kind="ordem_servico"
            title="Ordem de Serviço SST"
            description="Ordem de Serviço de Segurança e Saúde do Trabalho."
          />
        </TabsContent>
        <TabsContent value="ficha_registro" className="mt-4">
          <AdminKitDocSimpleTab
            adminToken={adminToken} kind="ficha_registro"
            title="Ficha de Registro"
            description="Ficha de Registro de Empregados (CLT Art. 41) — modelo AVI moderno."
          />
        </TabsContent>
      </Tabs>

      <KitUploadProgressDialog
        open={progressOpen}
        onOpenChange={setProgressOpen}
        uploadId={progressUploadId}
        adminToken={adminToken}
        onFinished={(s) => {
          toast.success(
            `Kit processado: ${s.matched_count} identificado(s)` +
            (s.not_found_count
              ? `, ${s.not_found_count} não localizado(s)`
              : ""),
          );
        }}
      />

      <KitSmartSplitDialog
        open={smartSplitOpen}
        onOpenChange={setSmartSplitOpen}
        adminToken={adminToken}
        onDone={(s) => {
          toast.success(
            `Smart Splitter: ${s?.ok_count || 0} documento(s) criado(s)` +
            ((s?.failed_count || 0)
              ? `, ${s.failed_count} falharam`
              : ""),
          );
        }}
      />
    </div>
  );
};

export default AdminAdmissionKitsTab;
